import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import { formatDateRange } from "@/utils/formatUtils";
import { isDefined } from "@/utils/utils";
import { Descriptions, Flex, Form, Modal, Popover } from "antd";
import { DescriptionsItemType } from "antd/es/descriptions";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEnumeration } from "../../../../common/api/queries";
import ActionButton from "../../../../common/components/buttons/ActionButton";
import AntIcon from "../../../../common/components/icons/AntIcon";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import { formatAgentDirectorateName } from "../../../agent/utils";
import { EnumerationType } from "../../../enumerations/enums";
import { ClientExtendedFilterFormFields, ExtendedClientFilterPageRequest } from "./ClientExtendedFilterFormFields";

type ModalFieldsForm = Pick<
  ExtendedClientFilterPageRequest,
  | "types"
  | "createdAtRange"
  | "managedByDirectorateIds"
  | "contactClientDaysMin"
  | "contactClientDaysMax"
  | "hasPartnerAgent"
  | "partnerAgentDirectorateIds"
  | "partnerAgentIdNumber"
>;

/**
 * `additionalFields` lists the remaining form fields of a criterion displayed as one item but stored
 * in several, so that clearing the filter resets all of them.
 */
type FilterResultItem = DescriptionsItemType & { additionalFields?: (keyof ModalFieldsForm)[] };

const filterItem = (
  key: keyof ModalFieldsForm | string,
  label: string,
  children?: string,
  additionalFields?: (keyof ModalFieldsForm)[],
): FilterResultItem | undefined => (children ? { key, label, children, additionalFields } : undefined);

type Props = {
  onSubmit: (values: ModalFieldsForm) => void;
};

export const ClientExtendedFilterModalForm = ({ onSubmit }: Props) => {
  const { t } = useTranslation();

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const directoratesEnums = useEnumeration(EnumerationType.AGENT_DIRECTORATES);

  const parentFormValues = Form.useFormInstance<ExtendedClientFilterPageRequest>().getFieldsValue();
  const [form] = Form.useForm<ModalFieldsForm>();

  useEffect(() => {
    if (isFilterModalOpen) {
      form.setFieldsValue(parentFormValues);
    }
  }, [isFilterModalOpen, form, parentFormValues]);

  const formatDirectorates = (directorateIds?: string[]) =>
    directorateIds
      ?.map((id) => {
        const directorate = directoratesEnums.find((item) => item.id === id);

        return directorate ? formatAgentDirectorateName(directorate) : "";
      })
      .join(", ");

  const { contactClientDaysMin, contactClientDaysMax } = parentFormValues;
  const contactClientDays =
    isDefined(contactClientDaysMin) && isDefined(contactClientDaysMax)
      ? `${contactClientDaysMin} ~ ${contactClientDaysMax}`
      : isDefined(contactClientDaysMin)
        ? `${t("common.from")} ${contactClientDaysMin}`
        : isDefined(contactClientDaysMax)
          ? `${t("common.to")} ${contactClientDaysMax}`
          : undefined;

  const activeFilterItems = [
    filterItem(
      "types",
      t("client.enums.type._label"),
      parentFormValues.types?.map((type) => t(`client.enums.type.${type}`)).join(", "),
    ),
    filterItem(
      "createdAtRange",
      t("client.filter.createdAtRange"),
      parentFormValues.createdAtRange?.[0] && parentFormValues.createdAtRange[1]
        ? formatDateRange(parentFormValues.createdAtRange[0], parentFormValues.createdAtRange[1])
        : undefined,
    ),
    filterItem(
      "managedByDirectorateIds",
      t("client.filter.managedByDirectorateIds"),
      formatDirectorates(parentFormValues.managedByDirectorateIds),
    ),
    filterItem(
      "hasPartnerAgent",
      t("client.enums.booleanFilterHasPartnerAgent._label"),
      parentFormValues.hasPartnerAgent
        ? t(`client.enums.booleanFilterHasPartnerAgent.${parentFormValues.hasPartnerAgent}`)
        : undefined,
    ),
    filterItem(
      "partnerAgentDirectorateIds",
      t("client.filter.partnerAgentDirectorateIds"),
      formatDirectorates(parentFormValues.partnerAgentDirectorateIds),
    ),
    filterItem("partnerAgentIdNumber", t("client.filter.partnerAgentIdNumber"), parentFormValues.partnerAgentIdNumber),
    filterItem("contactClientDaysMin", t("client.filter.contactClientDays"), contactClientDays, [
      "contactClientDaysMax",
    ]),
  ].filter((item) => item !== undefined);

  return (
    <>
      <ClientExtendedFilterFormFields hidden />

      <Flex align="center">
        <SecondaryButton icon={<AntIcon type="search" />} onClick={() => setIsFilterModalOpen(true)}>
          {t("common.extendedFilter")}
        </SecondaryButton>

        <div className="margin-left-small">
          {activeFilterItems.length ? (
            <>
              <Popover
                placement="bottom"
                title={t("client.helpers.activeFilters")}
                content={<Descriptions bordered size="small" column={1} items={activeFilterItems} />}
              >
                <span className="sub-header-info margin-right-tiny cursor-pointer">
                  {t("client.helpers.extendedFilterActiveHint")} <AntIcon type="question" />
                </span>
              </Popover>

              <ActionButton
                variant="text"
                color="danger"
                icon="delete"
                onClick={() => {
                  const clearedValues: Record<string, undefined> = {};

                  activeFilterItems.forEach((item) => {
                    [item.key as keyof ModalFieldsForm, ...(item.additionalFields ?? [])].forEach((field) => {
                      clearedValues[field] = undefined;
                    });
                  });

                  onSubmit(clearedValues as ModalFieldsForm);
                }}
              >
                {t("common.cancel")}
              </ActionButton>
            </>
          ) : undefined}
        </div>
      </Flex>

      <Modal
        width={ModalSizes.MEDIUM}
        open={isFilterModalOpen}
        title={t("common.extendedFilter")}
        onCancel={() => setIsFilterModalOpen(false)}
        onOk={() =>
          form.validateFields().then((values) => {
            onSubmit(values);
            setIsFilterModalOpen(false);
          })
        }
        okText={t("common.filter")}
        okButtonProps={{ icon: <AntIcon type="search" /> }}
        cancelText={t("common.cancel")}
        afterClose={() => form.resetFields()}
        mask={{ closable: false }}
      >
        <Form form={form} layout="vertical" name="clientExtendedFilterForm">
          <div style={{ display: "grid", gap: rowGutter, alignItems: "end", gridTemplateColumns: "1fr 1fr" }}>
            <ClientExtendedFilterFormFields />
          </div>
        </Form>
      </Modal>
    </>
  );
};
